import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Search, Receipt, Users, CircleDot } from "lucide-react";
import { useLocaleFormat } from "@/lib/i18n-format";
import { useFilterByOwnerCompany } from "@/owner/OwnerCompanyContext";
import { CompanyBadge } from "@/owner/CompanyBadge";

export const Route = createFileRoute("/_app/invoices/")({
  component: InvoicesPage,
});

type Status = "draft" | "sent" | "paid" | "partial" | "overdue" | "cancelled";

type Row = {
  id: string;
  number: string;
  title: string;
  status: Status;
  total: number;
  amount_paid: number;
  currency: string;
  issue_date: string;
  due_date: string | null;
  client_id: string;
  company_id: string;
  clients: { name: string } | null;
};

const variant: Record<Status, "default" | "secondary" | "destructive" | "outline"> = {
  draft: "secondary",
  sent: "outline",
  paid: "default",
  partial: "outline",
  overdue: "destructive",
  cancelled: "secondary",
};

function InvoicesPage() {
  const { t } = useTranslation();
  const { formatMoney, formatDate } = useLocaleFormat();
  const navigate = useNavigate();
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [clientFilter, setClientFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");

  const statusOptions: Status[] = ["draft", "sent", "paid", "partial", "overdue", "cancelled"];

  useEffect(() => {
    (async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from("invoices")
        .select(
          "id, number, title, status, total, amount_paid, currency, issue_date, due_date, client_id, company_id, clients(name)",
        )
        .order("created_at", { ascending: false });
      if (error) toast.error(t("pages.common.loadFailed"), { description: error.message });
      else setRows((data ?? []) as unknown as Row[]);
      setLoading(false);
    })();
  }, [t]);

  const scopedRows = useFilterByOwnerCompany(rows);

  const clientOptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const r of scopedRows) {
      if (r.client_id && r.clients?.name) map.set(r.client_id, r.clients.name);
    }
    return Array.from(map.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [scopedRows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = scopedRows;
    if (clientFilter !== "all") {
      list = list.filter((r) => r.client_id === clientFilter);
    }
    if (statusFilter !== "all") {
      list = list.filter((r) => r.status === statusFilter);
    }
    if (!q) return list;
    return list.filter((r) =>
      [r.number, r.title, r.clients?.name]
        .filter(Boolean)
        .some((v) => v!.toLowerCase().includes(q)),
    );
  }, [scopedRows, search, clientFilter, statusFilter]);

  const totals = useMemo(() => {
    let outstanding = 0;
    let overdue = 0;
    let paid = 0;
    scopedRows.forEach((r) => {
      const remain = Math.max(Number(r.total) - Number(r.amount_paid), 0);
      if (r.status === "paid") paid += Number(r.total);
      else if (r.status === "overdue") overdue += remain;
      else if (r.status !== "cancelled" && r.status !== "draft") outstanding += remain;
    });
    return { outstanding, overdue, paid };
  }, [scopedRows]);

  const fm = (v: number, c = "USD") => formatMoney(v, c);

  const currency = rows[0]?.currency || "USD";

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t("pages.invoices.title")}</h1>
          <p className="text-sm text-muted-foreground">{t("pages.invoices.subtitle")}</p>
        </div>
        <Button onClick={() => navigate({ to: "/invoices/new" })} className="gap-2">
          <Plus className="h-4 w-4" />
          {t("pages.invoices.newButton")}
        </Button>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <StatCard
          label={t("pages.invoices.outstanding")}
          value={fm(totals.outstanding, currency)}
          tone="default"
        />
        <StatCard
          label={t("pages.invoices.overdue")}
          value={fm(totals.overdue, currency)}
          tone="destructive"
        />
        <StatCard
          label={t("pages.invoices.paid")}
          value={fm(totals.paid, currency)}
          tone="success"
        />
      </div>

      <div className="rounded-lg border border-border bg-card">
        <div className="flex flex-col gap-2 border-b border-border p-3 sm:flex-row sm:items-center">
          <div className="flex flex-1 items-center gap-2">
            <Search className="h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={t("pages.common.searchPlaceholder")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8 border-0 bg-transparent shadow-none focus-visible:ring-0"
            />
          </div>
          <Select value={clientFilter} onValueChange={setClientFilter}>
            <SelectTrigger className="h-8 w-full sm:w-[200px]">
              <div className="flex items-center gap-2">
                <Users className="h-3.5 w-3.5 text-muted-foreground" />
                <SelectValue placeholder={t("pages.invoices.headers.client")} />
              </div>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("pages.common.all", "Todos")}</SelectItem>
              {clientOptions.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-8 w-full sm:w-[160px]">
              <div className="flex items-center gap-2">
                <CircleDot className="h-3.5 w-3.5 text-muted-foreground" />
                <SelectValue placeholder={t("pages.invoices.headers.status")} />
              </div>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("pages.common.all", "Todos")}</SelectItem>
              {statusOptions.map((s) => (
                <SelectItem key={s} value={s}>
                  {t(`statuses.${s}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Badge variant="secondary" className="sm:ml-2">
            {t("pages.invoices.count", { count: filtered.length })}
          </Badge>
        </div>

        {loading ? (
          <div className="flex items-center justify-center p-12">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-3 p-12 text-center">
            <div
              className="flex h-12 w-12 items-center justify-center rounded-full text-primary-foreground"
              style={{ background: "var(--gradient-brand)" }}
            >
              <Receipt className="h-6 w-6" />
            </div>
            <div>
              <p className="font-medium">
                {rows.length === 0 ? t("pages.invoices.empty") : t("pages.invoices.noMatch")}
              </p>
              <p className="text-sm text-muted-foreground">
                {rows.length === 0
                  ? t("pages.invoices.emptyDesc")
                  : t("pages.common.tryDifferentSearch")}
              </p>
            </div>
            {rows.length === 0 && (
              <Button onClick={() => navigate({ to: "/invoices/new" })} className="gap-2">
                <Plus className="h-4 w-4" />
                {t("pages.invoices.newButton")}
              </Button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("pages.invoices.headers.number")}</TableHead>
                  <TableHead>{t("pages.invoices.headers.client")}</TableHead>
                  <TableHead>{t("pages.invoices.headers.status")}</TableHead>
                  <TableHead className="hidden md:table-cell">
                    {t("pages.invoices.headers.due")}
                  </TableHead>
                  <TableHead className="text-right">{t("pages.invoices.headers.total")}</TableHead>
                  <TableHead className="text-right">
                    {t("pages.invoices.headers.balance")}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((r) => {
                  const balance = Math.max(Number(r.total) - Number(r.amount_paid), 0);
                  return (
                    <TableRow key={r.id} className="cursor-pointer">
                      <TableCell className="font-mono text-xs">
                        <Link
                          to="/invoices/$invoiceId"
                          params={{ invoiceId: r.id }}
                          className="hover:underline"
                        >
                          {r.number}
                        </Link>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Link
                            to="/invoices/$invoiceId"
                            params={{ invoiceId: r.id }}
                            className="font-medium hover:underline"
                          >
                            {r.clients?.name ?? "—"}
                          </Link>
                          <CompanyBadge companyId={r.company_id} />
                        </div>
                        <p className="text-xs text-muted-foreground">{r.title}</p>
                      </TableCell>
                      <TableCell>
                        <Badge variant={variant[r.status]}>{t(`statuses.${r.status}`)}</Badge>
                      </TableCell>
                      <TableCell className="hidden md:table-cell text-sm text-muted-foreground">
                        {r.due_date ? formatDate(r.due_date) : "—"}
                      </TableCell>
                      <TableCell className="text-right font-semibold">
                        {fm(r.total, r.currency)}
                      </TableCell>
                      <TableCell className="text-right text-sm">
                        <span className={balance > 0 ? "font-medium" : "text-muted-foreground"}>
                          {fm(balance, r.currency)}
                        </span>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone: "default" | "destructive" | "success";
}) {
  const toneClass =
    tone === "destructive"
      ? "text-destructive"
      : tone === "success"
        ? "text-emerald-600 dark:text-emerald-400"
        : "text-foreground";
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className={`mt-1 text-2xl font-semibold ${toneClass}`}>{value}</p>
    </div>
  );
}
